/**
 * Live battle updates: one realtime client per storefront (null when the stage has no Event
 * API, and the pages poll instead), a hook per battle page and one for the lobby, reconciling
 * events into the react-query cache. Summary events are hints — apply, then refetch the gated
 * battle; `ROUND_REVEALED` merges directly so the reel never waits on a fetch. Each hook returns
 * whether its subscription is live (acknowledged by the server), which is the one signal a page
 * may stop polling on: a connected socket whose subscribe was refused delivers nothing.
 */
import {
  type BattleResponse,
  battleChannel,
  battleEventSchema,
  battleLobbyChannel,
  type StorefrontRealtime,
} from "@loot-os/api-contract";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";

import { applyRoundRevealed, detectRoundGap } from "./battleTimeline.js";
import { qk, realtimeTokenQueryOptions } from "./queries.js";
import { queryClient } from "./queryClient.js";
import { createRealtimeClient, type RealtimeClient } from "./realtime.js";
import { useTenant } from "./tenant.js";

let holder: { key: string; client: RealtimeClient } | null = null;
let viewer = "anon";

/**
 * Who the realtime token is minted for: the signed-in shopper's id, or "anon". A change
 * reconnects with a token for the new viewer, which is what lets a shopper subscribe to their own
 * notification channel after signing in.
 */
export function setRealtimeViewer(next: string): void {
  if (next === viewer) return;
  viewer = next;
  holder?.client.reauthenticate();
}

/** The storefront's realtime client, or null when this stage publishes nothing (`realtime` is ""). */
export function getRealtimeClient(realtime: StorefrontRealtime | undefined): RealtimeClient | null {
  if (!realtime?.httpEndpoint || !realtime.realtimeEndpoint) return null;
  const key = `${realtime.httpEndpoint}|${realtime.realtimeEndpoint}`;
  if (holder?.key !== key) {
    holder?.client.close();
    holder = {
      key,
      client: createRealtimeClient({
        getAuth: async ({ fresh }) => {
          if (fresh) {
            await queryClient.invalidateQueries({
              queryKey: qk.realtimeTokens,
              refetchType: "none",
            });
          }
          const token = await queryClient.fetchQuery(realtimeTokenQueryOptions(viewer));
          return {
            httpEndpoint: token.httpEndpoint,
            realtimeEndpoint: token.realtimeEndpoint,
            token: token.token,
            expiresAtMs: Date.parse(token.expiresAt),
          };
        },
        onError: (error) => Sentry.captureException(error),
        onSocketError: () =>
          Sentry.addBreadcrumb({
            category: "realtime",
            level: "warning",
            message: "realtime socket error",
          }),
      }),
    };
  }
  return holder.client;
}

let lobbyInvalidateTimer: ReturnType<typeof setTimeout> | null = null;
function invalidateLobbySoon(): void {
  if (lobbyInvalidateTimer) return;
  lobbyInvalidateTimer = setTimeout(() => {
    lobbyInvalidateTimer = null;
    void queryClient.invalidateQueries({ queryKey: ["battles", "list"] });
  }, 500);
}

/** Reconciles one battle's events into its cached response. Exported for tests. */
export function handleBattleChannelEvent(battleId: string, raw: unknown): void {
  const parsed = battleEventSchema.safeParse(raw);
  if (!parsed.success) return;
  const event = parsed.data;
  if (event.type === "ROUND_REVEALED") {
    const prev = queryClient.getQueryData<BattleResponse>(qk.battle(battleId));
    if (prev && !detectRoundGap(prev.battle.currentRevealedRound, event.round)) {
      queryClient.setQueryData(qk.battle(battleId), applyRoundRevealed(prev, event));
    } else {
      void queryClient.invalidateQueries({ queryKey: qk.battle(battleId) });
    }
  } else {
    queryClient.setQueryData<BattleResponse>(qk.battle(battleId), (prev) =>
      prev ? { ...prev, battle: { ...prev.battle, ...event.battle } } : prev,
    );
    void queryClient.invalidateQueries({ queryKey: qk.battle(battleId) });
    if (event.type === "BATTLE_COMPLETED") {
      void queryClient.invalidateQueries({ queryKey: qk.myBattles });
    }
  }
  invalidateLobbySoon();
}

/** Subscribes to one battle's channel while mounted; true while that subscription is live. */
export function useBattleChannel(battleId: string | null | undefined): boolean {
  const { config } = useTenant();
  const client = getRealtimeClient(config.realtime);
  const environmentId = config.environmentId;
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!client || !battleId) return;
    const unsubscribe = client.subscribe(
      battleChannel(environmentId, battleId),
      (event) => handleBattleChannelEvent(battleId, event),
      (status) => setLive(status === "live"),
    );
    return () => {
      unsubscribe();
      setLive(false);
    };
  }, [client, environmentId, battleId]);
  return live;
}

/**
 * Subscribes to the environment's lobby channel while mounted; every event refreshes the list.
 * True while that subscription is live.
 */
export function useLobbyChannel(enabled = true): boolean {
  const { config } = useTenant();
  const client = getRealtimeClient(config.realtime);
  const environmentId = config.environmentId;
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!client || !enabled) return;
    const unsubscribe = client.subscribe(
      battleLobbyChannel(environmentId),
      () => invalidateLobbySoon(),
      (status) => setLive(status === "live"),
    );
    return () => {
      unsubscribe();
      setLive(false);
    };
  }, [client, environmentId, enabled]);
  return live;
}
